import { useState } from "react";
import { motion } from "framer-motion";
import { Play, X } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";

// Default YouTube videos - replace VIDEO_ID with your actual YouTube video IDs
const defaultVideos = [
  {
    id: "1",
    title: "Wedding Cinematic Film | Priya & Rahul",
    thumbnail: "https://images.unsplash.com/photo-1606216794079-73f85bbd57d5?w=800&q=85",
    youtubeId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    category: "wedding",
    description: "A beautiful celebration of love captured in cinematic style",
  },
  {
    id: "2",
    title: "Pre-Wedding Film | Mumbai",
    thumbnail: "https://images.unsplash.com/photo-1605368504600-f2cca6ea012a?w=800&q=85",
    youtubeId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    category: "pre-wedding",
    description: "Romantic pre-wedding shoot at iconic Mumbai locations",
  },
  {
    id: "3",
    title: "Wedding Highlights | Royal Celebration",
    thumbnail: "https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=85",
    youtubeId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    category: "wedding",
    description: "Grand wedding celebration highlights",
  },
  {
    id: "4",
    title: "Corporate Event Coverage",
    thumbnail: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=85",
    youtubeId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    category: "event",
    description: "Professional corporate event videography",
  },
  {
    id: "5",
    title: "Engagement Ceremony Film",
    thumbnail: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=800&q=85",
    youtubeId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    category: "event",
    description: "Intimate engagement ceremony beautifully captured",
  },
  {
    id: "6",
    title: "Pre-Wedding | Jaipur Palace",
    thumbnail: "https://images.unsplash.com/photo-1711506119180-e08899c7cdb1?w=800&q=85",
    youtubeId: "dQw4w9WgXcQ", // Replace with actual YouTube video ID
    category: "pre-wedding",
    description: "Royal pre-wedding shoot at historic Jaipur palace",
  },
];

const categories = [
  { id: "all", label: "All Videos" },
  { id: "wedding", label: "Wedding Films" },
  { id: "pre-wedding", label: "Pre-Wedding" },
  { id: "event", label: "Events" },
];

export default function Videos() {
  const [videos] = useState(defaultVideos);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedVideo, setSelectedVideo] = useState(null);

  const filteredVideos = activeCategory === "all"
    ? videos
    : videos.filter((video) => video.category === activeCategory);

  const openVideo = (video) => {
    setSelectedVideo(video);
    document.body.style.overflow = "hidden";
  };

  const closeVideo = () => {
    setSelectedVideo(null);
    document.body.style.overflow = "auto";
  };

  return (
    <main data-testid="videos-page" className="pt-24">
      {/* Hero Section */}
      <section data-testid="videos-hero" className="section pb-12">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-gold text-sm uppercase tracking-[0.3em] mb-4 block">
              Cinematic Stories
            </span>
            <h1 className="font-serif text-5xl md:text-7xl font-medium text-white mb-6">
              Video Gallery
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Watch our cinematic wedding films and event coverage that capture 
              emotions, moments, and stories in stunning detail.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section data-testid="videos-filter" className="pb-12">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                data-testid={`video-filter-${category.id}`}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 text-sm uppercase tracking-widest transition-all duration-300 ${
                  activeCategory === category.id
                    ? "bg-gold text-black font-bold"
                    : "bg-transparent border border-white/20 text-white/70 hover:border-gold hover:text-gold"
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Videos Grid */}
      <section data-testid="videos-grid-section" className="pb-24">
        <div className="container-custom">
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredVideos.map((video, index) => (
              <motion.div
                key={video.id}
                layout
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                data-testid={`video-item-${index}`}
                className="group cursor-pointer"
                onClick={() => openVideo(video)}
              >
                {/* Thumbnail */}
                <div className="relative aspect-video overflow-hidden mb-4">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-colors duration-300 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-gold/90 group-hover:bg-gold flex items-center justify-center transition-all duration-300 group-hover:scale-110">
                      <Play size={28} className="text-black ml-1" fill="currentColor" />
                    </div>
                  </div>
                  {/* Category Badge */}
                  <span className="absolute top-4 left-4 bg-black/70 text-gold text-xs uppercase tracking-widest px-3 py-1">
                    {video.category}
                  </span>
                </div>
                {/* Info */}
                <h3 className="font-serif text-xl text-white group-hover:text-gold transition-colors duration-300 mb-2">
                  {video.title}
                </h3>
                <p className="text-white/50 text-sm">{video.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {filteredVideos.length === 0 && (
            <div className="text-center py-20">
              <p className="text-white/60 text-lg">No videos found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Video Modal */}
      {selectedVideo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="video-modal"
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={closeVideo}
        >
          {/* Close Button */}
          <button
            data-testid="video-modal-close"
            onClick={closeVideo}
            className="absolute top-6 right-6 text-white/70 hover:text-white z-10 transition-colors"
          >
            <X size={32} strokeWidth={1} />
          </button>

          {/* Video Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative aspect-video bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.youtubeId}?autoplay=1&rel=0`}
                title={selectedVideo.title}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="mt-6 text-center">
              <h3 className="font-serif text-2xl text-white mb-2">{selectedVideo.title}</h3>
              <p className="text-white/50">{selectedVideo.description}</p>
            </div>
          </motion.div>
        </motion.div>
      )}

      <WhatsAppButton />
    </main>
  );
}
